import type { InventoryCategoryRecord } from '@/renderer/types/inventory';
import { Button, DropdownSelect, Modal } from '@components/common';
import { FormInput, FormTextarea } from '@components/common/FormField';
import React, { useEffect, useState } from 'react';
import {
  createEmptyInventoryCategoryForm,
  createInventoryCategoryFormFromRecord,
  InventoryCategoryFormState,
} from './inventorySettingsForm';

interface InventoryCategoryEditorModalProps {
  category: InventoryCategoryRecord | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (form: InventoryCategoryFormState) => Promise<void>;
}

export function InventoryCategoryEditorModal({
  category,
  isSaving,
  onClose,
  onSave,
}: InventoryCategoryEditorModalProps) {
  const [form, setForm] = useState<InventoryCategoryFormState>(
    createEmptyInventoryCategoryForm(),
  );

  useEffect(() => {
    setForm(
      category
        ? createInventoryCategoryFormFromRecord(category)
        : createEmptyInventoryCategoryForm(),
    );
  }, [category]);

  const update = <K extends keyof InventoryCategoryFormState>(
    key: K,
    value: InventoryCategoryFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={category ? 'Edit inventory category' : 'Create inventory category'}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Category name"
            value={form.name}
            onChange={(event) => update('name', event.target.value)}
            placeholder="Example: Beverages"
            required
          />
          <DropdownSelect
            label="Status"
            value={form.isActive ? 'active' : 'inactive'}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            onChange={(value) => update('isActive', value === 'active')}
          />
        </div>

        <FormTextarea
          label="Description"
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
          placeholder="Optional note about what belongs in this category"
          rows={3}
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave(form);
            }}
            disabled={!form.name.trim()}
            isLoading={isSaving}
          >
            {category ? 'Save changes' : 'Create category'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
